import { ref, computed } from 'vue';
import { useSpotifyAuth } from './useSpotifyAuth';
import { useSpotifyApi } from './useSpotifyApi';
import { useSpotifyPlayer } from './useSpotifyPlayer';
import { useAudioPreview } from './useAudioPreview';
import { parseQrToTrackUri } from '@/services/qrParser';
import type { GameMode, SpotifyTrack } from '@/types/spotify';

// Globaler State
const mode = ref<GameMode>('none');
const currentTrackUri = ref<string | null>(null);
const currentTrackMeta = ref<SpotifyTrack | null>(null);
const isPlaying = ref(false);
const hasUserPressedPlayButton = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);

export function useGameState() {
  const { isPremium, isAuthenticated } = useSpotifyAuth();
  const { getTrackByUri } = useSpotifyApi();
  const spotifyPlayer = useSpotifyPlayer();
  const audioPreview = useAudioPreview();

  /**
   * Setzt den Spielmodus
   */
  function setMode(newMode: GameMode) {
    mode.value = newMode;

    // Bei Premium-Modus: Player vorbereiten (aber Sound erst bei Play-Klick)
    if (newMode === 'premium' && isPremium.value) {
      spotifyPlayer.connect();
    }
  }

  /**
   * Verarbeitet einen gescannten QR-Code
   */
function isMobileBrowser(): boolean {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return /android|iphone|ipad|ipod|opera mini|iemobile|wpdesktop/i.test(ua.toLowerCase());
}

async function handleScan(qrValue: string) {
  isLoading.value = true;
  error.value = null;

  try {
    const trackUri = parseQrToTrackUri(qrValue);
    currentTrackUri.value = trackUri;

    const trackData = await getTrackByUri(trackUri);
    if (!trackData) {
      throw new Error('Track konnte nicht geladen werden');
    }

    currentTrackMeta.value = trackData;

    const mobile = isMobileBrowser();

    if (!mobile && isPremium.value && isAuthenticated.value) {
      // Desktop + Premium → voller Song über Web Playback
      setMode('premium');
      await spotifyPlayer.connect();
    } else {
      // Mobile ODER kein Premium → 30s Preview
      setMode('preview');
      if (trackData.preview_url) {
        audioPreview.load(trackData.preview_url);
      } else {
        throw new Error('Kein Preview verfügbar für diesen Track');
      }
    }

    hasUserPressedPlayButton.value = false;
    isPlaying.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Unbekannter Fehler';
    console.error('Fehler beim Scannen:', err);
  } finally {
    isLoading.value = false;
  }
}

  /**
   * Startet die Wiedergabe
   */
  async function play() {
    if (!currentTrackUri.value || !currentTrackMeta.value) {
      console.warn('[GameState.play] Kein Track geladen');
      return;
    }

    hasUserPressedPlayButton.value = true;
    error.value = null;

    console.log('[GameState.play] mode =', mode.value);
    console.log('[GameState.play] uri =', currentTrackUri.value);

    try {
      if (mode.value === 'premium') {
        console.log('[GameState.play] Versuche Spotify-Webplayer zu verbinden...');
        const ok = await spotifyPlayer.connect();
        console.log('[GameState.play] connect() returned:', ok);
        if (!ok) {
          throw new Error('Spotify Player konnte nicht verbunden werden');
        }

        console.log('[GameState.play] Starte Wiedergabe über Web Playback...');
        await spotifyPlayer.playTrack(currentTrackUri.value);
        console.log('[GameState.play] playTrack() erfolgreich');
        isPlaying.value = true;
        console.log('[GameState.play] Song läuft!');
      } else if (mode.value === 'preview') {
        console.log('[GameState.play] Preview-Mode. preview_url =', currentTrackMeta.value.preview_url);
        if (!currentTrackMeta.value.preview_url) {
          throw new Error('Keine Preview-URL verfügbar');
        }

        await audioPreview.play();
        console.log('[GameState.play] audioPreview.play() erfolgreich');
        isPlaying.value = true;
      }
    } catch (err) {
      console.error('[GameState.play] Fehler beim Abspielen:', err);
      error.value = err instanceof Error ? err.message : 'Fehler beim Abspielen des Songs';
      isPlaying.value = false;
    }
  }

  /**
   * Pausiert die Wiedergabe
   */
  async function pause() {
    hasUserPressedPlayButton.value = false;

    try {
      if (mode.value === 'premium') {
        await spotifyPlayer.pause();
      } else if (mode.value === 'preview') {
        audioPreview.pause();
      }
    } catch (err) {
      console.error('[GameState.pause] Fehler beim Pausieren:', err);
    }

    isPlaying.value = false;
  }

  /**
   * Setzt das Spiel zurück (neue Karte)
   */
  function reset() {
    console.log('[GameState.reset] Reset Game');
    // Wiedergabe stoppen
    if (mode.value === 'preview') {
      audioPreview.stop();
    } else if (mode.value === 'premium') {
      // Fehler beim Pausieren ignorieren
      spotifyPlayer.pause().catch((err) =>
        console.warn('[GameState.reset] Fehler beim Pausieren während reset():', err)
      );
    }

    currentTrackUri.value = null;
    currentTrackMeta.value = null;
    hasUserPressedPlayButton.value = false;
    isPlaying.value = false;
    error.value = null;
  }

  const canPlay = computed(() => {
    return !!currentTrackUri.value && !isLoading.value;
  });

  return {
    mode: computed(() => mode.value),
    currentTrackUri: computed(() => currentTrackUri.value),
    currentTrackMeta: computed(() => currentTrackMeta.value),
    isPlaying: computed(() => isPlaying.value),
    hasUserPressedPlayButton: computed(() => hasUserPressedPlayButton.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    canPlay,

    setMode,
    handleScan,
    play,
    pause,
    reset
  };
}
